/**
 * A picture from the library.
 *
 * The picture endpoint is authenticated, because a school's library must not be
 * reachable by anyone who can guess a URL. But an `<img src>` cannot carry an
 * `Authorization` header, so pointing one straight at the endpoint gets a 401 and
 * a broken image on every screen - which is exactly what happened.
 *
 * Two other ways were considered and rejected:
 *
 * * a token in the query string - it would appear in browser history, in proxy
 *   logs and in any `Referer` header the page sends;
 * * a cookie for this one endpoint - it would reintroduce a CSRF surface that
 *   bearer tokens do not have.
 *
 * So the bytes are fetched with the header and handed to the browser as an object
 * URL. Content-addressed storage means the bytes at an id never change, so the
 * shared URL cache can live for the session.
 */

import type { ImgHTMLAttributes } from 'react'
import { usePictureUrl } from './pictureUrl'

type Props = Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'id'> & {
  id: string | null | undefined
  alt?: string
}

export function Picture({ id, alt = '', ...rest }: Props) {
  const url = usePictureUrl(id)
  if (!url) return null
  return <img src={url} alt={alt} {...rest} />
}
